import React, { useEffect, useState } from "react";
import AISection from "~/components/settings/HamburgerMenu/sections/AISection";
import CameraSection from "~/components/settings/HamburgerMenu/sections/CameraSection";
import ConversationSection from "~/components/settings/HamburgerMenu/sections/ConversationSection";
import DictionarySection from "~/components/settings/HamburgerMenu/sections/DictionarySection";
import KnowledgeSection from "~/components/settings/HamburgerMenu/sections/KnowledgeSection";
import VoiceSection from "~/components/settings/HamburgerMenu/sections/VoiceSection";
import SidebarLayout from "~/components/settings/HamburgerMenu/SidebarLayout";
import { Character, VRMList } from "shared/Schemas";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { languageAtom, T2SEngineAtom, volumeAtom } from "~/Atom";
import {
  createCharacter,
  getVRMFiles,
  getVRMName,
  getVRMURL,
  sortVRMsByCharacterOrder,
} from "~/vrm_utils/VRMUtils";
import { selectByName } from "~/components/db/CharacterDBUtils";
import { useAtom } from "jotai";
import { VRM, VRMLoaderPlugin } from "@pixiv/three-vrm";
import { VRMAnimationLoaderPlugin } from "@pixiv/three-vrm-animation";
import { currentLocale, t2SEngines } from "~/Commons";
import * as db from "~/components/db/CharacterDBUtils";
import ChatHistoryGraphSection from "~/components/settings/HamburgerMenu/sections/ChatHistoryGraphSection";
import LoadingScreen from "~/components/LoadingScreen";
import { PlaySound } from "~/SoundUtils";
import SystemSection from "~/components/settings/HamburgerMenu/sections/SystemSection";
import FloorMapSection from "~/components/settings/HamburgerMenu/sections/FloorMapSection";
import QRCodeSection from "~/components/settings/HamburgerMenu/sections/QRCodeSection";
import ActivationSection from "~/components/settings/HamburgerMenu/sections/ActivationSection";
import PhonebookAppSettingsDownloadSection from "~/components/settings/HamburgerMenu/sections/PhonebookAppSettingsDownloadSection";
import { useAuth } from "~/components/auth/CognitoAuth";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import { useTranslation } from "react-i18next";
import "~/css/CognitoAuth.css";

const isOnPrem = process.env.REACT_APP_SYSTEM_REQUIREMENT === "onprem";

const SettingPage: React.FC = () => {
  const [loadedVrm, setLoadedVrm] = useState<VRM | null>(null);
  const [loadedVrmList, setLoadedVrmList] = useState<VRM[]>([]);
  const [language] = useAtom(languageAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [volume] = useAtom(volumeAtom);
  const { signOut } = useAuth();
  const loader = new GLTFLoader();
  loader.register((parser) => {
    return new VRMLoaderPlugin(parser);
  });
  loader.register((parser) => {
    return new VRMAnimationLoaderPlugin(parser);
  });

  // VRMの情報を取得し、VRMをロードする関数
  const loadVRM = async ({ url, name }: VRMList): Promise<VRM | null> => {
    return new Promise((resolve) => {
      if (!url) {
        console.error("モデルのパスがありません");
        resolve(null);
        return;
      }
      loader.load(
        url,
        async (tmpGltf) => {
          try {
            const vrm = tmpGltf.userData.vrm;
            if (vrm === null) {
              resolve(null);
              return;
            }

            const globalName = name.replace(/\.vrm$/i, "");
            const searchName = getVRMName(vrm);
            const character = await selectByName(searchName);
            if (character === null) {
              console.error("CharacterNotFound：キャラクターを新規作成します");
              await createCharacter(searchName, globalName, "VOICEVOX");
            }
            resolve(vrm);
          } catch (error) {
            console.error("VRM処理エラー:", error);
            resolve(null);
          }
        },
        undefined,
        (error) => {
          console.error("An error happened");
          console.error(error);
          resolve(null);
        }
      );
    });
  };

  // ttsEngine設定
  const setTTSEngine = async (vrm: VRM | null) => {
    const characters = (await db.selectAll()) as Character[];
    const character = characters.find((character) => character.name === getVRMName(vrm));

    const ttsEngine = character?.tts_engine;
    if (language === currentLocale && ttsEngine) {
      setT2SEngine(ttsEngine);
    } else {
      setT2SEngine(t2SEngines.openai);
    }
  };

  // キャラクターアイコンクリック時の処理
  const handleCharacterSelect = (vrm: VRM) => {
    setLoadedVrm(vrm);
    setTTSEngine(vrm);
    PlaySound("push", volume);
  };

  // ログアウト処理
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("ログアウトエラー:", error);
    }
  };

  // VRMの情報を取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        // VRMの情報を取得
        const vrmData = (await getVRMFiles()) as VRMList[];
        const characters = await db.selectAvailableCharacters(vrmData);
        for (const vrm of vrmData) {
          vrm.url = getVRMURL(vrmData, vrm.name);
        }

        // VRMをロード
        const results = await Promise.all(vrmData.map((vrm) => loadVRM(vrm)));

        // charactersの登録順にVRMをソート
        const filteredResults = sortVRMsByCharacterOrder(results, characters);

        setLoadedVrm(filteredResults.length > 0 ? filteredResults[0] : null);
        setTTSEngine(filteredResults.length > 0 ? filteredResults[0] : null);
        setLoadedVrmList(filteredResults);
      } catch (error) {
        console.error("Error fetching idle motion buffer:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {loadedVrm ? (
        <div className="App-page">
          {/* ヘッダーセクション */}
          <div className="settings-header">
            <div className="settings-header-content">
              <h1 className="settings-title">設定</h1>
              <div className="settings-header-right">
                <div className="settings-user-info">
                  <UserNameEditor />
                </div>
                <button onClick={handleLogout} className="settings-logout-button">
                  <span className="material-symbols-outlined">logout</span>
                  <span>ログアウト</span>
                </button>
              </div>
            </div>
          </div>

          <div className="settings-main-content">
            <SidebarLayout pageMode={true}>
              <AISection
                loadedVrm={loadedVrm}
                loadedVrmList={loadedVrmList}
                handleCharacterSelect={handleCharacterSelect}
              />
              <VoiceSection loadedVrm={loadedVrm} isExpression={false} />
              <ConversationSection />
              <CameraSection />
              <KnowledgeSection />
              <DictionarySection />
              <ChatHistoryGraphSection />
              <SystemSection />
              <FloorMapSection />
              <QRCodeSection />
              <ActivationSection />
              <PhonebookAppSettingsDownloadSection />
            </SidebarLayout>
          </div>
        </div>
      ) : (
        <LoadingScreen />
      )}
    </>
  );
};

/**
 * 表示名編集コンポーネント
 * Cognito認証済みの場合はCognitoのname||username属性を表示させる
 * オンプレ版の場合はlocalStorageの"administrator"を表示させる
 */
const UserNameEditor: React.FC = () => {
  const { user, updateName } = useAuth();
  const { t } = useTranslation();
  const [volume] = useAtom(volumeAtom);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [displayName, setDisplayName] = useState<string>(
    isOnPrem ? localStorage.getItem("administrator") || "管理者" : ""
  );

  /**
   * 表示名を取得する
   * オンプレ版の場合はキャッシュ済みdisplayNameを返す
   * Cognito認証済みの場合はCognitoのname||username属性を表示させる
   * @returns 表示名
   */
  const getDisplayName = (): string => {
    if (isOnPrem) return displayName || "管理者";
    return user?.name || user?.username || "管理者";
  };

  /**
   * 表示名編集切り替え処理
   * 現在の表示名をinputにセットして編集モードに切り替える
   */
  const handleStartEditName = () => {
    setEditingName(getDisplayName());
    setIsEditingName(true);
    PlaySound("push", volume);
  };

  /**
   * 表示名のバリデーション
   * @param value 検証する文字列
   * @returns エラーメッセージ、または問題なければnull
   */
  const validateName = (value: string): string | null => {
    if (value.trim().length === 0) return t("inputValidation", { attribute: t("name") });
    if (value.length > 15) return t("maxLengthValidation", { attribute: t("name"), max: 15 });
    return null;
  };

  /**
   * 表示名をバックエンドに保存する
   * @param name 保存する表示名
   */
  const saveNameUpdate = async (name: string): Promise<void> => {
    if (isOnPrem) {
      localStorage.setItem("administrator", name);
      setDisplayName(name);
    } else {
      await updateName(name);
    }
  };

  /**
   * 表示名編集完了処理
   * バリデーションを行い、問題なければバックエンドに保存する
   * 処理の成否に応じて通知ダイアログを表示する
   * @returns void
   */
  const handleNameBlur = async () => {
    setIsEditingName(false);
    const trimmed = editingName.trim();
    if (trimmed === getDisplayName()) return;
    const validationError = validateName(trimmed);
    if (validationError) {
      showNotificationDialog(validationError, "warning", "closable");
      return;
    }
    try {
      await saveNameUpdate(trimmed);
      showNotificationDialog(t("registerSuccess"), "success", "closable");
    } catch {
      showNotificationDialog(t("registerFailure"), "error", "closable");
    }
  };

  if (isEditingName) {
    return (
      <input
        type="text"
        name="edit-username"
        className="settings-username-input"
        value={editingName}
        maxLength={15}
        autoFocus
        onChange={(e) => setEditingName(e.target.value)}
        onBlur={handleNameBlur}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        aria-label="表示名を編集"
      />
    );
  }

  return (
    <>
      <button
        type="button"
        className="settings-user-icon-button"
        onClick={handleStartEditName}
        aria-label="表示名を編集する"
      >
        <span className="material-symbols-outlined">person</span>
      </button>
      <span>{getDisplayName()}</span>
    </>
  );
};

export default SettingPage;
